/* eslint-disable no-use-before-define */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Input,
  FormControl,
  FormLabel,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Flex
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { createUsersApi } from '../../utils/api-interface'
import { getErrorMessage } from '../../utils/utils'
import { log } from '../../utils/logger'
import { UserRegistrationRequest } from '../../services/network'
import dayjs from 'dayjs'

interface ICreateUserModalProps {
  isOpen: boolean
  onClose: () => void
  onUserCreated: () => void
}

function CreateUserModal({ isOpen, onClose, onUserCreated }: ICreateUserModalProps): JSX.Element {
  const { t } = useTranslation()
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [dateOfBirth, setDateOfBirth] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const [loading, isLoading] = useState<boolean>(false)

  const toast = useToast()

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => setFirstName(e.target.value)
  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => setLastName(e.target.value)
  const handleDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>): void => setDateOfBirth(e.target.value)
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => setEmail(e.target.value)
  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>): void => setUsername(e.target.value)

  useEffect(() => {
    eva.replace()
  })

  const isFormComplete = useMemo(() => {
    return firstName !== '' && lastName !== '' && dateOfBirth !== '' && email !== '' && username !== ''
  }, [firstName, lastName, dateOfBirth, email, username])

  const handleClose = useCallback(() => {
    setFirstName('')
    setLastName('')
    setDateOfBirth('')
    setEmail('')
    setUsername('')
    onClose()
  }, [onClose])

  const isBirthDateValid = useMemo(() => {
    if (!dateOfBirth.trim()) return false
    const birthDate = dayjs(dateOfBirth)
    const todayMinus18Years = dayjs().subtract(18, 'year')
    return birthDate.isValid() && birthDate.isBefore(todayMinus18Years)
  }, [dateOfBirth])

  const isEmailValid = useMemo(() => {
    const trimmedEmail = email.trim()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(trimmedEmail)
  }, [email])

  const isUsernameValid = useMemo(() => {
    const trimmedUsername = username.trim()
    const usernameRegex = /^[a-zA-Z0-9_-]+$/
    return usernameRegex.test(trimmedUsername)
  }, [username])

  const buttonDisabled = useMemo(() => {
    return !isFormComplete || !isBirthDateValid || !isEmailValid || !isUsernameValid || loading
  }, [isBirthDateValid, isEmailValid, isFormComplete, isUsernameValid, loading])

  const buildUserRegistrationRequest = useCallback((): UserRegistrationRequest | undefined => {
    const trimmedFirstName = firstName.trim()
    const trimmedLastName = lastName.trim()
    const trimmedUsername = username.trim()
    const trimmedBirthDate = dateOfBirth.trim()
    const trimmedEmail = email.trim()

    if (!trimmedFirstName || !trimmedLastName || !trimmedUsername || !trimmedBirthDate || !trimmedEmail) {
      return undefined
    }

    if (buttonDisabled) {
      return undefined
    }

    return {
      firstName: trimmedFirstName,
      lastName: trimmedLastName,
      username: trimmedUsername,
      birthDate: trimmedBirthDate,
      email: trimmedEmail
    }
  }, [firstName, lastName, username, dateOfBirth, email, buttonDisabled])

  const createNewUser = useCallback(async () => {
    if (buttonDisabled) {
      log.debug(`[CreateUserModal] Can't create user. Invalid data`)
      return
    }
    const request: UserRegistrationRequest | undefined = buildUserRegistrationRequest()
    if (!request) {
      return
    }

    isLoading(true)

    try {
      await createUsersApi().adminRegisterUser(request)
      toast({
        title: t('createUserModal.success'),
        status: 'success',
        duration: 2000,
        isClosable: true
      })
      onUserCreated()
      handleClose()
    } catch (e) {
      log.error(`[CreateModalUser] createNewUser: ${e}`)
      toast({
        title: t('createUserModal.error'),
        description: getErrorMessage(e),
        status: 'error',
        duration: 2000,
        isClosable: true
      })
    }

    isLoading(false)
  }, [buildUserRegistrationRequest, buttonDisabled, handleClose, onUserCreated, t, toast])

  const getDateOfBirthError = useCallback((): string | undefined => {
    const trimmed = dateOfBirth.trim()
    const date = dayjs(trimmed)
    const minDate = dayjs().subtract(18, 'year')

    if (!trimmed) return t('createUserModal.date_error.required')
    if (!date.isValid()) return t('createUserModal.date_error.wrong_format')
    if (!date.isBefore(minDate)) return t('createUserModal.date_error.underage')
    return undefined
  }, [dateOfBirth, t])

  const getUsernameError = useCallback((): string | undefined => {
    const trimmed = username.trim()
    const regex = /^[a-zA-Z0-9_-]+$/

    if (!trimmed) return t('createUserModal.username_error.required')
    if (!regex.test(trimmed)) return t('createUserModal.username_error.wrong_format')
    return undefined
  }, [t, username])

  const getEmailError = useCallback((): string | undefined => {
    const trimmed = email.trim()
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!trimmed) return t('createUserModal.email_error.required')
    if (!regex.test(trimmed)) return t('createUserModal.email_error.wrong_format')
    return undefined
  }, [email, t])

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered={true}>
        <ModalOverlay />

        <ModalContent marginBottom={'0px'} borderRadius={'26px 26px 0px 0px'}>
          <Flex alignItems='left' direction={'column'}>
            <ModalHeader>
              <Flex align='center' justify='space-between' width='100%'>
                <Text color={'black'} fontWeight='700' fontSize='24px' whiteSpace='nowrap'>
                  {t('createUserModal.title')}
                </Text>
              </Flex>
            </ModalHeader>

            <ModalBody overflowY={'auto'} maxH={'350px'}>
              <Flex direction={'column'} rowGap={2}>
                <FormControl id='first_name'>
                  <FormLabel variant={'primary'}>{t('createUserModal.first_name')}</FormLabel>
                  <Input bg='white' value={firstName} onChange={handleFirstNameChange} />
                </FormControl>
                <FormControl id='last_name'>
                  <FormLabel variant={'primary'}>{t('createUserModal.last_name')}</FormLabel>
                  <Input bg='white' value={lastName} onChange={handleLastNameChange} />
                </FormControl>

                <FormControl id='username'>
                  <FormLabel variant={'primary'}>{t('createUserModal.username')}</FormLabel>
                  <Input bg='white' value={username} onChange={handleUsernameChange} isInvalid={username !== '' && !isUsernameValid} />
                  {username !== '' && !isUsernameValid && <Text variant={'FormError'}>{getUsernameError()}</Text>}
                </FormControl>

                <FormControl id='email'>
                  <FormLabel variant={'primary'}>{t('createUserModal.email')}</FormLabel>
                  <Input type='email' bg='white' value={email} onChange={handleEmailChange} isInvalid={email !== '' && !isEmailValid} />
                  {email !== '' && !isEmailValid && <Text variant={'FormError'}>{getEmailError()}</Text>}
                </FormControl>
                <FormControl id='dateofbirth'>
                  <FormLabel variant={'primary'}>{t('createUserModal.date')}</FormLabel>
                  <Input type='date' bg='white' value={dateOfBirth} onChange={handleDateOfBirthChange} isInvalid={dateOfBirth !== '' && !isBirthDateValid} />
                  {dateOfBirth !== '' && !isBirthDateValid && <Text variant={'FormError'}>{getDateOfBirthError()}</Text>}
                </FormControl>
              </Flex>
            </ModalBody>

            <ModalFooter justifyContent='center'>
              <Button
                variant='outline'
                width={'50%'}
                height='50px'
                color={'primary.400'}
                borderColor={'primary.400'}
                onClick={handleClose}
                margin={'5px'}
                isDisabled={loading}>
                {t('cancel')}
              </Button>
              <Button variant={'primary'} width={'50%'} height='50px' isDisabled={buttonDisabled} isLoading={loading} onClick={createNewUser} margin={'5px'}>
                {t('create')}
              </Button>
            </ModalFooter>
          </Flex>
        </ModalContent>
      </Modal>
    </>
  )
}

export { CreateUserModal }
